import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { Box, Button, Card, CardContent, Divider, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../../context";
import { useProfile } from "../../../context/ProfileContext";
import { FerramentaDetalhe } from "../../../shared/components";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { useVForm, VForm } from "../../../shared/forms";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { useClientDetails } from "../../../shared/hooks/useClientDetails";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { validationPermission } from "../../../shared/utils/validationPermission";
import { useCepLookup } from "../hooks/useCepLookup";
import { useClientDocuments } from "../hooks/useClientDocuments";
import { useSaveClient } from "../hooks/useSaveClient";
import { getDefaultClientFormData } from "../utils/defaultClientForm";
import { getFollowUpServiceByContext } from "./ModalFollowUpGeneric/FollowUpService";
import { GenericFollowUpModalContent } from "./ModalFollowUpGeneric/GenericFollowUpModalContent";
import { ModalDocumentList } from "./Modals/ModalDocumentList";
import { AddressClient } from "./createComponents/AddressClient";
import { BankClient } from "./createComponents/BankClient";
import { ClientRelationsTabs } from "./createComponents/ClientRelationsTabs";
import { GenerateClient } from "./createComponents/GenerateClient";
import { ModalLogs } from "./createComponents/Modals/ModalLogs";
import { ParamsClient } from "./createComponents/Parametrizes";
import { SetupChargeConfigCard } from "./createComponents/SetupChargeConfigCard";
import { TenantSolidarityParams } from "./createComponents/TenantSolidarityParams";

export const DetalheClientesPresentation: React.FC = () => {
	const { id = "novo" } = useParams<"id">();
	const navigate = useNavigate();
	const { formRef, save, saveAndClose, issaveAndClose } = useVForm();
	const [nome, setNome] = useState("");
	const [clientType, setClientType] = useState("legal");
	const { isSuperAdmin, permissionLevel, permissions, isPermissionInitialized } = useProfile();
	const { loggedUser } = useAuthContext();
	const [openModalLogs, setOpenModalLogs] = useState(false);
	const [openModalFollowUp, setOpenModalFollowUp] = useState(false);
	const [activeSignature, setActiveSignature] = useState(false);
	const [signContract, setSignContract] = useState(true);
	const [activeSetup, setActiveSetup] = useState(false);
	const [bonusActive, setBonusActive] = useState(false);
	const [comissionActive, setComissionActive] = useState(false);
	const [baseRateCalc, setBaseRateCalc] = useState("aluguel");
	const [creditCard, setCreditCard] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const followUpServices = getFollowUpServiceByContext("client");
	const [openModalDocumentos, setOpenModalDocumentos] = useState(false);
	const [activeSignatureAvulse, setActiveSignatureAvulse] = useState(false);
	const [tenantSolidarity, setTenantSolidarity] = useState(false);
	const { checkCEP } = useCepLookup(formRef);

	const { handleSave } = useSaveClient({
		id,
		formRef,
		issaveAndClose,
		setIsLoading,
		activeSignature,
		signContract,
		activeSetup,
		bonusActive,
		comissionActive,
		creditCard,
		tenantSolidarity,
	});

	const { isLoading: isLoadingClientData, row: clientData, updateCreatedFrom } = useClientDetails({
		id,
		formRef,
		setNome,
		setInitialStates: (data) => {
			setActiveSignature(data.activeSignature === 1);
			setSignContract(data.signContract === "sign");
			setActiveSetup(data.tax_setup_enable === 1);
			setBonusActive(data.bonusActive === "active");
			setComissionActive(data.comissionActive === "active");
			setCreditCard(data.creditCard === "active");
			setActiveSignatureAvulse(data.signatureAvulse === "active");
			setTenantSolidarity(data.tenantSolidarity === "active");
		},
	});

	const { documents: documentList, fetchDocuments } = useClientDocuments(id);

	const handleOpenModalDocumentos = async () => {
		await fetchDocuments();
		setOpenModalDocumentos(true);
	};

	useEffect(() => {
		if (id === "novo") {
			formRef.current?.setData(getDefaultClientFormData());
		}
	}, [id]);

	const handleClientType = (e: any) => {
		setClientType(e.target.value);
		formRef.current?.setFieldValue("document", " ");
	};

	function useClientPermissions() {
		return {
			canEdit: checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Update),
			canAdd: checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Add),
			canViewLogs: isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.ViewClientLogs),
		};
	}

	const { canEdit, canAdd, canViewLogs } = useClientPermissions();
	const canActivatePartners =
		isPermissionInitialized &&
		(isSuperAdmin || validationPermission(permissions, PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.ActivatePartners));
	const canChangePartnerOrigin =
		isPermissionInitialized &&
		(isSuperAdmin || validationPermission(permissions, PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.ChangePartnerOrigin));

	return (
		<LayoutBaseDePagina
			titulo="Clientes"
			subtitulo={id === "novo" ? "Cadastro de novo cliente" : `Alteração de cliente [ ${nome} ]`}
			barraDeFerramentas={
				<FerramentaDetalhe
					textoButtonNew="novo"
					showBotaoApagar={false}
					showBotaoSalvar={!isLoading && canEdit}
					showBotaoSalvarFechar={!isLoading && canEdit}
					showButtonNew={id !== "novo" && canAdd}
					showButtonFollowUp={!isLoading && isSuperAdmin}
					showBotaoDocumentos={!isLoading && canEdit && id !== "novo"}
					clicarSalvar={save}
					clicarSalvarFechar={saveAndClose}
					clicarNovo={() => navigate("/clientes/detalhe/novo")}
					clicarVoltar={() => navigate("/clientes")}
					clickButtonFollowUp={() => setOpenModalFollowUp(true)}
					clicarDocumentos={handleOpenModalDocumentos}
				/>
			}
		>
			<Box sx={{ width: "100%", maxWidth: "1400px", px: 2 }}>
				<VForm ref={formRef} onSubmit={handleSave} onPointerEnterCapture={""} onPointerLeaveCapture={() => { }} placeholder={""}>
					<Card sx={{ mb: 3 }}>
						<CardContent>
							<GenerateClient ClientType={clientType} handleClientType={handleClientType} isLoading={isLoading} setNome={setNome} />
							<Divider sx={{ my: 2 }} />
							<BankClient isLoading={isLoading} />
							<Divider sx={{ my: 2 }} />
							<AddressClient checkCEP={checkCEP} isLoading={isLoading} />
							<Divider sx={{ my: 2 }} />
							<ClientRelationsTabs
								id={id}
								isSuperAdmin={isSuperAdmin}
								permissionLevel={permissionLevel ?? 1000}
								isLoading={isLoading}
								canActivatePartners={canActivatePartners}
								canChangePartnerOrigin={canChangePartnerOrigin}
								createdFrom={clientData?.created_from}
								createdFromPartnerId={clientData?.partnerId}
								onCreatedFromUpdated={updateCreatedFrom}
							/>
							<Divider sx={{ my: 2 }} />
							<ParamsClient
								baseRateCalc={baseRateCalc}
								setBaseRateCalc={setBaseRateCalc}
								isLoading={isLoading}
								isSuperAdmin={isSuperAdmin}
								activeSignature={activeSignature}
								setActiveSignature={setActiveSignature}
								signContract={signContract}
								setSignContract={setSignContract}
								permissionLevel={permissionLevel}
								activeSetup={activeSetup}
								setActiveSetup={setActiveSetup}
								setBonusActive={setBonusActive}
								setComissionActive={setComissionActive}
								bonusActive={bonusActive}
								comissionActive={comissionActive}
								creditCard={creditCard}
								setCreditCard={setCreditCard}
								activeSignatureAvulse={activeSignatureAvulse}
								setActiveSignatureAvulse={setActiveSignatureAvulse}
							/>
							<TenantSolidarityParams
								isLoading={isLoading}
								tenantSolidarity={tenantSolidarity}
								setTenantSolidarity={setTenantSolidarity}
							/>
						</CardContent>
					</Card>

					{canViewLogs && (
						<Card sx={{ mb: 2 }}>
							<CardContent>
								<Box display="flex" justifyContent="space-between" alignItems="center">
									<Typography variant="h6" fontWeight={400} color="secondary">
										Logs do Cliente
									</Typography>
									<Button variant="outlined" color="info" startIcon={<RemoveRedEyeOutlinedIcon />} onClick={() => setOpenModalLogs(true)}>
										Visualizar Logs
									</Button>
								</Box>
							</CardContent>
						</Card>
					)}
				</VForm>

				{id !== "novo" && canEdit && (isSuperAdmin || (permissionLevel ?? 1000) <= 1) && <SetupChargeConfigCard clientId={id} canEdit={canEdit} />}
			</Box>

			{canViewLogs && <ModalLogs isOpen={openModalLogs} onClose={() => setOpenModalLogs(false)} id={id} />}

			{openModalFollowUp && (
				<GenericFollowUpModalContent
					open={openModalFollowUp}
					onClose={() => setOpenModalFollowUp(false)}
					clientId={id}
					loggedUser={loggedUser!}
					services={followUpServices}
					permissionSend={true}
					permissionAddContact={true}
				/>
			)}

			<ModalDocumentList
				isOpen={openModalDocumentos}
				onClose={() => setOpenModalDocumentos(false)}
				documents={documentList}
				clientId={id}
				onUploadSuccess={() => fetchDocuments()}
			/>
		</LayoutBaseDePagina>
	);
};